// WebSocket连接管理器 - 使用STOMP协议
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import toastManager from './toastManager';

class WebSocketManager {
  constructor() {
    this.stompClient = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3秒后开始重连
    this.reconnectTimeout = null;
    this.messageHandlers = new Map(); // 消息类型到处理函数的映射
    this.subscriptions = new Map(); // 主题订阅映射
    this.url = this.getWebSocketUrl();
    
    // 初始化默认消息处理
    this.initializeMessageHandlers();
  }

  // 根据当前API地址生成WebSocket地址
  getWebSocketUrl() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    // 使用HTTP地址，SockJS会自动处理
    return `${apiUrl.replace(/\/$/, '')}/ws`;
  }

  // 初始化默认消息处理函数
  initializeMessageHandlers() {
    // 处理通知消息
    this.addMessageHandler('notification', (data) => {
      console.log('收到通知消息:', data);
      // 使用toastManager显示通知
      toastManager.show(data.message, data.type || 'info', data.duration || 3000);
    });

    // 处理错误消息
    this.addMessageHandler('error', (data) => {
      console.error('WebSocket错误消息:', data);
      toastManager.error(data.message, 5000);
    });

    // 处理系统消息
    this.addMessageHandler('system', (data) => {
      console.log('系统消息:', data);
      let message = '';
      // 根据消息类型构建通知内容
      if (data.type === 'sign_in_sheet_created' && data.phone) {
        message = `号码为 ${data.phone} 的客户已到店`;
      } else {
        // 其他类型消息或无法识别的消息
        message = data.message || JSON.stringify(data);
      }
      toastManager.info(message, 4000);
    });
  }

  // 添加消息类型处理函数
  addMessageHandler(type, handler) {
    this.messageHandlers.set(type, handler);
  }

  // 移除消息类型处理函数
  removeMessageHandler(type) {
    this.messageHandlers.delete(type);
  }

  // 订阅STOMP主题
  subscribe(topic, callback) {
    if (!this.isConnected || !this.stompClient || !this.stompClient.connected) {
      console.error('WebSocket未连接或连接未完全建立，无法订阅主题');
      return null;
    }

    try {
      // 检查是否已经订阅了该主题，避免重复订阅
      for (const [existingId, subscription] of this.subscriptions.entries()) {
        if (subscription.topic === topic) {
          console.log(`【WebSocket 订阅】已存在对 ${topic} 的订阅 (ID: ${existingId})，跳过重复订阅`);
          return existingId;
        }
      }

      // 使用STOMP客户端订阅主题
      const stompSubscription = this.stompClient.subscribe(topic, (message) => {
        try {
          let data = JSON.parse(message.body);
          callback(data);
        } catch (error) {
          console.error('解析订阅消息失败:', error);
          callback(message.body);
        }
      });

      // 生成唯一的订阅ID
      const subscriptionId = `sub-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      // 在客户端保存订阅信息
      this.subscriptions.set(subscriptionId, { 
        topic, 
        callback, 
        stompSubscription 
      });
      
      console.log(`【WebSocket 订阅】已订阅主题: ${topic} (ID: ${subscriptionId})`);
      console.log(`【WebSocket 订阅】当前所有订阅:`, Array.from(this.subscriptions.keys()).map(id => 
        `${this.subscriptions.get(id).topic} (ID: ${id})`
      ));
      
      return subscriptionId;
    } catch (error) {
      console.error('订阅STOMP主题失败:', error);
      return null;
    }
  }

  // 取消订阅STOMP主题
  unsubscribe(subscriptionId) {
    if (!this.isConnected || !this.stompClient) {
      console.error('WebSocket未连接，无法取消订阅主题');
      return false;
    }

    try {
      // 检查订阅是否存在
      if (!this.subscriptions.has(subscriptionId)) {
        console.error('订阅ID不存在:', subscriptionId);
        return false;
      }
      
      const subscription = this.subscriptions.get(subscriptionId);
      
      // 使用STOMP客户端取消订阅
      if (subscription.stompSubscription) {
        subscription.stompSubscription.unsubscribe();
      }
      
      // 从客户端移除订阅信息
      this.subscriptions.delete(subscriptionId);
      console.log(`【WebSocket 取消订阅】已取消订阅主题: ${subscription.topic} (ID: ${subscriptionId})`);
      console.log(`【WebSocket 取消订阅】剩余订阅:`, Array.from(this.subscriptions.keys()).map(id => 
        `${this.subscriptions.get(id).topic} (ID: ${id})`
      ));
      
      return true;
    } catch (error) {
      console.error('取消订阅STOMP主题失败:', error);
      return false;
    }
  }

  // 连接WebSocket
  connect() {
    if (this.isConnected) {
      console.log('WebSocket已连接，跳过重复连接');
      return;
    }

    try {
      // 创建STOMP客户端
      this.stompClient = new Client({
        webSocketFactory: () => new SockJS(this.url),
        onConnect: () => this.handleOpen(),
        onMessage: (message) => this.handleMessage(message),
        onStompError: (frame) => this.handleError(frame),
        onDisconnect: () => this.handleClose(),
        reconnectDelay: this.reconnectDelay,
        maxReconnectAttempts: this.maxReconnectAttempts
      });

      // 启动连接
      this.stompClient.activate();

    } catch (error) {
      console.error('WebSocket连接创建失败:', error);
      this.attemptReconnect();
    }
  }

  // 断开WebSocket连接
  disconnect() {
    if (this.stompClient) {
      // 使用STOMP客户端的deactivate方法断开连接
      this.stompClient.deactivate();
      this.stompClient = null;
    }
    
    // 清空订阅列表
    if (this.subscriptions.size > 0) {
      console.log('【WebSocket 清理】连接断开，取消所有订阅');
      this.subscriptions.clear();
    }
    
    this.isConnected = false;
    this.clearReconnectTimeout();
  }

  // 处理连接打开
  handleOpen() {
    console.log('WebSocket连接已打开');
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.clearReconnectTimeout();
    
    // 确保stompClient完全连接后再执行操作
    const executeInitialization = () => {
      if (this.stompClient && this.stompClient.connected) {
        // 连接成功后发送初始化消息
        this.send('ping', { timestamp: Date.now() });
        
        // 自动订阅系统主题
        this.subscribe('/topic/system', (message) => {
          console.log('【系统主题消息】:', message);
          // 调用系统消息处理函数
          if (this.messageHandlers.has('system')) {
            const handler = this.messageHandlers.get('system');
            handler(message.data || message);
          }
        });
      } else {
        // 如果连接还未完全建立，等待一下再尝试
        console.log('等待STOMP客户端完全连接...');
        setTimeout(executeInitialization, 100);
      }
    };
    
    executeInitialization();
  }

  // 处理消息接收
  handleMessage(message) {
    try {
      // 打印完整的消息对象
      console.log('【WebSocket 完整请求】:', message);
      
      // STOMP客户端返回的消息包含headers和body
      const { headers, body } = message;
      console.log('【STOMP 消息头部】:', headers);
      console.log('【STOMP 消息体】:', body);
      
      // 获取消息主题
      const topic = headers.destination;
      console.log('【STOMP 消息主题】:', topic || '无主题');
      
      // 解析消息体
      let data = JSON.parse(body);
      console.log('【STOMP 解析后消息】:', data);
      console.log('【STOMP 消息类型】:', data.type || '未知');

      // 检查当前是否在商家签到列表页面
      const isMerchantSignInPage = window.location.pathname.startsWith('/merchant-sign-in');
      console.log('【当前页面】:', window.location.pathname);
      console.log('【是否商家签到列表页面】:', isMerchantSignInPage);

      // 只在商家签到列表页面处理通知
      if (isMerchantSignInPage) {
        // 获取路径上的search参数
        const searchParams = new URLSearchParams(window.location.search);
        const searchValue = searchParams.get('search');
        console.log('【路径搜索参数】:', searchValue);

        // 检查消息中是否有brand字段，并且等于路径上的search参数
        console.log('【Brand匹配检查】:', {
          messageBrand: data.brand,
          searchValue: searchValue,
          isMatch: data.brand && searchValue && data.brand === searchValue
        });
        
        if (data.brand && searchValue && data.brand === searchValue) {
          // 检查是否有phone字段用于通知内容
          console.log('【Phone字段检查】:', {
            hasPhone: !!data.phone,
            phoneValue: data.phone,
            phoneType: typeof data.phone
          });
          
          if (data.phone) {
            // 显示通知：号码为 xxxxxxx 的客户已到店
            // 硬编码测试消息，排查模板字符串问题
            const notificationMessage = "号码为 13892531375 的客户已到店";
            console.log('【客户到店通知】:', notificationMessage);
            console.log('【通知参数】:', {
              message: notificationMessage,
              duration: 5000,
              typeofMessage: typeof notificationMessage
            });
            toastManager.info(notificationMessage, 5000);
          } else {
            console.log('【通知失败】: 消息中没有phone字段');
          }
        } else {
          console.log('【通知失败】: Brand不匹配或search参数不存在');
        }
      }

      // 处理来自订阅主题的消息
      if (topic && this.subscriptions.size > 0) {
        // 这是来自订阅主题的消息
        console.log(`处理来自主题 ${topic} 的消息`);
        
        // 特别记录/topic/system主题的详细信息
        if (topic === '/topic/system') {
          console.log('【系统主题】收到消息:', JSON.stringify(data, null, 2));
          console.log('【系统主题】当前订阅列表:', Array.from(this.subscriptions.keys()).map(id => 
            `${this.subscriptions.get(id).topic} (ID: ${id})`
          ));
        }
      }
    } catch (error) {
      console.error('WebSocket消息解析失败:', error);
      toastManager.error('消息解析失败', 3000);
    }
  }

  // 处理连接错误
  handleError(frame) {
    console.error('WebSocket连接错误:', frame);
    toastManager.error('WebSocket连接错误', 3000);
  }

  // 处理连接关闭
  handleClose() {
    console.log('WebSocket连接已关闭');
    this.isConnected = false;

    // 尝试重连（STOMP客户端已内置重连机制，这里可以保留额外的重连逻辑或移除）
    this.attemptReconnect();
  }

  // 发送消息
  send(type, data) {
    if (!this.isConnected || !this.stompClient || !this.stompClient.connected) {
      console.error('WebSocket未连接或连接未完全建立，无法发送消息');
      return false;
    }

    try {
      const message = JSON.stringify({ type, data });
      // 使用STOMP客户端发送消息到服务器的应用目的地
      this.stompClient.publish({
        destination: '/app/message', // 根据后端配置调整
        body: message
      });
      console.log('发送WebSocket消息:', message);
      return true;
    } catch (error) {
      console.error('WebSocket消息发送失败:', error);
      return false;
    }
  }

  // 尝试重连
  attemptReconnect() {
    // STOMP客户端已内置重连机制，这里保留简化的重连逻辑作为后备
    console.log('WebSocket尝试重连...');
  }

  // 清除重连定时器
  clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  // 获取连接状态
  getStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }

  // 启动WebSocket连接
  start() {
    this.connect();
  }

  // 停止WebSocket连接
  stop() {
    this.disconnect();
  }
}

// 创建单例实例
const websocketManager = new WebSocketManager();

export default websocketManager;